import Swiper from "swiper";
import { Navigation, A11y } from "swiper/modules";
import "swiper/css";

const PC_MEDIA_QUERY = "(min-width: 1025px)";

export default class Pv {
    constructor() {
        this.init();
    }

    init() {
        this.pvEl = document.querySelector(".js-pv");
        if (!this.pvEl) return;

        this.player = null;
        this.isPlayerReady = false;
        this.backgroundVideoId = this.pvEl.querySelector(".pv__movie")?.getAttribute("data-videoID");
        this.isYTApiReady = false;
        this.playerElId = "js-youtube-player";

        this.setIntroInState();
        this.initSwiper();
        this.prepareYouTubeApi();
    }

    initSwiper() {
        const slider = this.pvEl.querySelector(".js-pv-swiper");
        if (!slider) return;

        this.swiper = new Swiper(slider, {
            modules: [Navigation, A11y],
            slidesPerView: 1,
            spaceBetween: 40 * Math.min(window.innerWidth / 780, 1),
            breakpoints: {
                781: { spaceBetween: 40 * Math.min(window.innerWidth / 1920, 1) },
            },
            navigation: {
                prevEl: this.pvEl.querySelector(".js-pv-prev"),
                nextEl: this.pvEl.querySelector(".js-pv-next"),
            },
            a11y: {
                prevSlideMessage: "前の動画へ",
                nextSlideMessage: "次の動画へ",
                slideLabelMessage: "{{index}} / {{slidesLength}}",
            },
        });

        // CSSの可変幅に合わせ、リサイズ後もカード間の余白を揃える。
        this.swiper.on("resize", () => {
            const viewport = window.innerWidth <= 780 ? 780 : 1920;
            this.swiper.params.spaceBetween = 40 * Math.min(window.innerWidth / viewport, 1);
            this.swiper.update();
        });
        this.swiper.on("slideChange", () => this.syncBackgroundVideo());
    }

    syncBackgroundVideo() {
        const slide = this.swiper?.slides[this.swiper.activeIndex];
        const videoId = slide?.querySelector(".pv__movie")?.getAttribute("data-videoID");
        // API読み込み中の切り替えは、onReadyで最新のアクティブスライドを反映する。
        if (!videoId || !this.isPlayerReady) return;
        if (videoId === this.backgroundVideoId) return;

        this.player.mute();
        this.player.loadVideoById(videoId);
        this.backgroundVideoId = videoId;
    }

    // PC時、ロード時点(top:0基準)でPVが画面内に入っていたら -introin を付与
    // (ロード時から見えている場合とスクロールで出現する場合とで演出タイミングを分けるためのフラグ。リサイズでは付け外ししない)
    // 判定はレイアウト確定後(load後)に行う。newsのload時再計算(rAF遅延)でmainvisualが拡張され
    // PVの位置が動くことがあるため。利用側のbody.-introendはload+3秒なので、それまでに確定していればよい
    setIntroInState() {
        if (!this.pvEl) return;
        if (!window.matchMedia(PC_MEDIA_QUERY).matches) return;

        const judge = () => {
            // 実行時にスクロールが0とは限らないため、top:0に居た場合の絶対位置で画面内判定する
            const topAtZero = this.pvEl.getBoundingClientRect().top + window.scrollY;
            if (topAtZero < window.innerHeight) {
                this.pvEl.classList.add("-introin");
            }
        };

        // newsのrAF遅延更新より後に測るため、2フレーム待ってから判定する
        const judgeAfterLayout = () => requestAnimationFrame(() => requestAnimationFrame(judge));

        if (document.readyState === "complete") {
            judgeAfterLayout();
            return;
        }

        window.addEventListener("load", judgeAfterLayout, { once: true });
    }

    // YouTube IFrame API 読み込み
    // iframeのパラメータで?loop=1&playlist=IDが効かず、「動画が再生できません」表示になるため、jsでループ制御実行
    prepareYouTubeApi() {
        if (window.YT && window.YT.Player) {
            this.isYTApiReady = true;
            this.ensurePlayer(); // 初期化
            return;
        }

        const prev = window.onYouTubeIframeAPIReady;
        window.onYouTubeIframeAPIReady = () => {
            if (typeof prev === "function") prev();
            this.isYTApiReady = true;
            this.ensurePlayer();
        };

        if (document.querySelector('script[data-yt-iframe-api="true"]')) return;

        const tag = document.createElement("script");
        tag.src = "https://www.youtube.com/iframe_api";
        tag.async = true;
        tag.dataset.ytIframeApi = "true";
        document.head.appendChild(tag);
    }

    ensurePlayer() {
        if (!this.isYTApiReady) return;
        if (this.player) return;

        const iframe = document.getElementById(this.playerElId);
        if (!iframe) return;

        this.player = new YT.Player(this.playerElId, {
            events: {
                onReady: (e) => {
                    this.isPlayerReady = true;
                    // 初期表示の動画もここで autoplay を試す
                    try {
                        e.target.mute();
                        this.syncBackgroundVideo();
                        e.target.playVideo();
                    } catch (err) {
                        // autoplayがブロックされる環境では失敗することがある
                    }
                },
                onStateChange: (e) => {
                    // ENDED = 0 → 疑似ループ
                    if (e.data === YT.PlayerState.ENDED) {
                        e.target.seekTo(0, true);
                        e.target.playVideo();
                    }
                },
            },
        });
    }
}
